import { MaxUint256 } from '@ethersproject/constants'
import { erc20 as ERC20Abi, jdiAuto } from '@jdiyield/abi'
//TODO remove platforms here
import { chainData, ChainID, jdiAutoListByChain, jdiAutoPoolList, oldplatforms as platforms } from '@jdiyield/constants'
import { getAPR, getBalanceNumber } from '@jdiyield/contracts'
import {
  APR,
  BaseBalance,
  Chain,
  Contract,
  JDIPoolAuto,
  Platform,
  PlatformName,
  PriceList,
  Stake,
  Token
} from '@jdiyield/interfaces'
import BigNumber from 'bignumber.js'
import Web3 from 'web3'

import { calculate } from '../calculate'
import getNodeUrl from '../rpcRandom'
import { StakeFunc, StakeProps } from '../types'
import { makeBatchRequest, safeGasEstimates } from '../utils'

export const abi = jdiAuto as any

export type VaultAPR = APR & {
  vaultTvlUSD?: number
}

const convertBigNumber = (amount: string) => {
  try {
    const number = new BigNumber(amount).times(1e18).toFixed()
    return `${number}`
  } catch (e) {
    console.log(e)
  }
}

export const getJDIAutoData = async (
  web3: Web3,
  chain: Chain,
  walletAddress: string,
  tokens: Token[],
  priceList?: PriceList
) => {
  try {
    const jdiList = jdiAutoListByChain(chain)
    const poolResult = await Promise.all(
      jdiList.map(async (item) => {
        //? New one
        const activePools = item.pool.filter((p) => p.isActive)

        const poolInfos = await makeBatchRequest(
          web3,
          activePools.map((i) => {
            const { poolId } = i
            const ct = new web3.eth.Contract(abi as any, i.contractAddress)
            return ct.methods.poolInfo(poolId).call
          })
        )
        //userInfo
        let batchChunk = []
        const _userInfos = await makeBatchRequest(
          web3,
          activePools.flatMap((i) => {
            const { poolId } = i
            const ct = new web3.eth.Contract(abi as any, i.contractAddress)
            const lpContract = new web3.eth.Contract(ERC20Abi as any, i.lpAddress)
            if (walletAddress) {
              batchChunk = [
                ct.methods.myUserInfo(poolId, walletAddress).call,
                lpContract.methods.balanceOf(walletAddress).call,
                lpContract.methods.allowance(walletAddress, i.contractAddress).call
              ]
            } else {
              batchChunk = []
            }

            return batchChunk
          })
        )

        const userInfos = walletAddress
          ? [...new Array(_userInfos.length / batchChunk.length)].map((_, index) => {
              const userInfoIndex = index * batchChunk.length
              const userInfo = _userInfos[userInfoIndex]
              const lpBalance = _userInfos[userInfoIndex + 1]
              const allowance = _userInfos[userInfoIndex + 2]

              return { userInfo, lpBalance, allowance }
            })
          : activePools.map(() => ({
              userInfo: '0',
              lpBalance: '0',
              allowance: false
            }))

        const result = await Promise.all(
          userInfos.map(async ({ userInfo, lpBalance, allowance }, index) => {
            const i = activePools[index]
            const poolInfo = poolInfos[index]
            const { lpAddress, poolId } = i
            const lp: BaseBalance = {
              pool: poolId,
              balance: userInfo,
              lpAddress: lpAddress,
              availableBalanceOf: lpBalance
            }
            const balanceLP = await calculate(web3, lp, i.routerAddress, tokens, priceList)

            const lpBalanceParams: BaseBalance = {
              pool: poolId,
              balance: lpBalance,
              lpAddress: lpAddress,
              availableBalanceOf: lpBalance
            }
            const lpBalanceUSD = await calculate(web3, lpBalanceParams, i.routerAddress, tokens, priceList)
            return {
              ...i,
              data: {
                userInfo: new BigNumber(userInfo).dividedBy(1e18).toFixed(),
                poolInfo,
                allowance,
                isApprove: +allowance > 0,
                lpBalance: new BigNumber(lpBalance).dividedBy(1e18).toFixed(),
                lpBalanceUSD,
                balanceLP,
                tokenDropdown: i.tokenDropdown || []
              }
            }
          })
        )
        // const lpContract = new web3.eth.Contract(ERC20Abi as any, i.lpAddress)

        //   return result
        return { ...item, data: result }
      })
    )
    return poolResult
  } catch (error) {
    console.log(error)
  }
}

export const getJDIVault = async (
  web3: Web3,
  walletAddress: string,
  ct: Contract,
  tokens: Token[],
  priceList: PriceList,
  chain?: Chain
): Promise<Stake[]> => {
  const result = await getJDIAutoData(web3, chain, walletAddress, tokens, priceList)

  return result.flatMap(({ data }) => {
    return data.map(({ data, supportIL, supportROI }) => ({
      ...ct,
      ...data.balanceLP,
      supportIL,
      supportROI
    }))
  })
}

export const stakeJDIAuto: StakeFunc = async ({ web3, pool, contractAddress, walletAddress, amount }) => {
  try {
    const ct = new web3.eth.Contract(abi as any, contractAddress)
    const contractWithMethod = ct.methods.deposit(pool, convertBigNumber(amount))
    const gasLimit = await safeGasEstimates({
      contractWithMethod,
      walletAddress
    })
    await contractWithMethod.send({ from: walletAddress, gas: gasLimit })
    return true
  } catch (e) {
    return false
  }
}

export const stakeJDIAutoByToken: StakeFunc = async ({
  web3,
  routerAddress,
  pool,
  contractAddress,
  walletAddress,
  tokenAddress,
  pathA = [],
  pathB = [],
  amount
}) => {
  try {
    const ct = new web3.eth.Contract(abi as any, contractAddress)

    const contractWithMethod = ct.methods.depositByToken(
      tokenAddress,
      pool,
      convertBigNumber(amount),
      routerAddress,
      pathA,
      pathB
    )

    await contractWithMethod.send({ from: walletAddress, value: convertBigNumber(amount) })
    return true
  } catch (e) {
    return false
  }
}

export const unStakeJDIAuto: StakeFunc = async ({ web3, pool, contractAddress, walletAddress, amount }) => {
  try {
    const ct = new web3.eth.Contract(abi as any, contractAddress)
    const contractWithMethod = ct.methods.withdraw(pool, convertBigNumber(amount))
    const gasLimit = await safeGasEstimates({
      contractWithMethod,
      walletAddress
    })
    await contractWithMethod.send({ from: walletAddress, gas: gasLimit })
    return true
  } catch (e) {
    return false
  }
}

export const unStakeAndRemoveLiquidityJDIAuto: StakeFunc = async ({
  web3,
  pool,
  contractAddress,
  walletAddress,
  routerAddress,
  amount
}) => {
  try {
    const ct = new web3.eth.Contract(abi as any, contractAddress)
    const contractWithMethod = ct.methods.withdrawAndRemoveLiquidity(pool, convertBigNumber(amount), routerAddress)
    const gasLimit = await safeGasEstimates({
      contractWithMethod,
      walletAddress
    })
    await contractWithMethod.send({ from: walletAddress, gas: gasLimit })
    return true
  } catch (e) {
    return false
  }
}

export const compoundJDIAuto: StakeFunc<Omit<StakeProps, 'amount'>> = async ({
  web3,
  pool,
  contractAddress,
  walletAddress
}) => {
  try {
    const ct = new web3.eth.Contract(abi as any, contractAddress)
    const contractWithMethod = ct.methods.compound(pool)
    const gasLimit = await safeGasEstimates({
      contractWithMethod,
      walletAddress
    })
    await contractWithMethod.send({ from: walletAddress, gas: gasLimit })
    return true
  } catch (e) {
    return false
  }
}

export const checkAllowance = async (
  lpAddress: string,
  contractAddress: string,
  walletAddress: string
): Promise<boolean> => {
  try {
    const web3 = await new Web3(Web3.givenProvider)
    const contract = new web3.eth.Contract(ERC20Abi as any, lpAddress)
    const result = await contract.methods.allowance(walletAddress, contractAddress).call()
    return +result > 0
  } catch (e) {
    return false
  }
}

export const approveContract = async (
  web3: Web3,
  lpAddress: string,
  contractAddress: string,
  walletAddress: string
): Promise<boolean> => {
  try {
    const contract = new web3.eth.Contract(ERC20Abi as any, lpAddress)
    const contractWithMethod = contract.methods.approve(contractAddress, MaxUint256)
    const gasLimit = await safeGasEstimates({
      contractWithMethod,
      walletAddress
    })

    await contractWithMethod.send({ gas: gasLimit, from: walletAddress })
    return true
  } catch (e) {
    return false
  }
}

export const getJDIStratInfo = async (
  poolId: number,
  chainID: ChainID
): Promise<{
  contractAddress: string
  functionRewardPerBlockName: string
  poolIdStrat: number
  getBonusMultiplier: string
  rewardAddress: string
  platform: Platform
}> => {
  const poolStrat = jdiAutoPoolList
    .filter((data) => data.chain === chainData[chainID].chainName)
    .flatMap((data) => data.pool)
    .find((pool) => pool.poolId === poolId)
  if (!poolStrat) return null
  const contractAddress = poolStrat.farmContract.split('/')[4]

  const contract = platforms
    .flatMap((platform) => platform?.contracts ?? [])
    .find((c) => c.contractAddress?.toLowerCase() === contractAddress?.toLowerCase())

  const platform = platforms.find((platform) =>
    platform?.contracts?.find((contract) => contract?.contractAddress?.toLowerCase() === contractAddress?.toLowerCase())
  )

  return {
    contractAddress,
    rewardAddress: contract?.rewardAddress,
    functionRewardPerBlockName: contract?.functionRewardPerBlockName,
    poolIdStrat: poolStrat.poolIdStrat,
    getBonusMultiplier: contract?.getBonusMultiplier,
    platform
  }
}

export const getJDIVaultAPR = async (
  platformName: PlatformName,
  platformData: Stake,
  vaultData: JDIPoolAuto,
  chainID: ChainID,
  tokens: Token[],
  priceList: PriceList
): Promise<VaultAPR> => {
  try {
    const aprData = await getAPR(
      platformName,
      platformData.contractAddress,
      platformData?.functionRewardPerBlockName,
      platformData.rewardAddress,
      platformData.lpAddress,
      platformData.pool,
      chainID,
      tokens,
      priceList,
      platformData?.getBonusMultiplier,
      platformData.lpUSD
    )

    const farmContract = vaultData.farmContract.split('/')[4]
    const vaultContract = vaultData.vaultContract.split('/')[4]

    const RPC_URL = getNodeUrl(chainID)
    const web3 = await new Web3(RPC_URL)
    const ct = new web3.eth.Contract(abi as any, farmContract)
    const vaultInfo = await ct.methods.userInfo(vaultData.poolIdStrat, vaultContract).call()
    const vaultTvl = vaultInfo[0]
    const vaultTvlUSD = +getBalanceNumber(new BigNumber(vaultTvl)) * aprData?.poolPrice ?? 0

    return { ...aprData, vaultTvlUSD }
  } catch (e) {
    console.log('getJDIVaultAPR', e)
    return {} as any
  }
}

//// DOCS ////

// stakeJDIAuto
// props =  pool, contractAddress, walletAddress, amount
// unStakeJDIAuto
// props = pool, contractAddress, walletAddress, amount

// unStakeAndRemoveLiquidityJDIAuto
// props =  pool,contractAddress,walletAddress,routerAddress,amount

// compoundJDIAuto
// props = pool,contractAddress,walletAddress

// hint ค่าต่างมีอยู่แล้วจาก /constants/jdiAuto.ts แค่ส่งมาให้ function ด้วย

// -pool = poolId
// -contractAddress = /// เอา contractAddress ส่งมา
// -walletAddress = /// เอา walletAddress ส่งมา
// -amount = จำนวน lp amount ที่ต้องการ
// -routerAddress = /// routerAddress

// =======
// approveContract
// props =
// - lpAddress: string,  /// เอา lpAddress ส่งมา
// - contractAddress: string, /// เอา contractAddress ส่งมา
// - walletAddress: string  /// เอา walletAddress ส่งมา
